import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Cloud, FilterX } from "lucide-react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MISOverview } from "./MISOverview";
import { MISPipelineSection } from "./MISPipelineSection";
import { InvoicedSalesSection } from "./InvoicedSalesSection";
import { MISInsights } from "./MISInsights";

const DCR_ZONE = {
  Delhi: "North", "BD N": "North", Ahmbd: "West", Mumbai: "West", Pune: "West",
  Blr: "South", Hybd: "South", Chennai: "South", "BD S": "South", HnI: "National",
};
const ZONES = ["All", "North", "South", "West", "Exports", "National"];
const SEGMENTS = ["All", "CRE", "HnI", "Govt", "Export", "OEM"];
const PRODUCTS = ["All", "HD", "VMT", "Univic"];

function Filter({ label, value, onChange, options, testId, width = "w-[130px]" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`h-8 ${width} text-xs`} data-testid={testId}>
        <span className="text-slate-400 mr-1">{label}:</span><SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {options.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function MISAnalytics() {
  const { data, isLoading } = useQuery({
    queryKey: ["mis-analytics"],
    queryFn: async () => (await api.get("/sales-mis/analytics")).data,
    refetchInterval: (q) => (q.state.data?.ready ? 300000 : 20000),
  });
  const [zone, setZone] = useState("All");
  const [branch, setBranch] = useState("All");
  const [segment, setSegment] = useState("All");
  const [person, setPerson] = useState("All");
  const [product, setProduct] = useState("All");
  const [inited, setInited] = useState(false);

  useEffect(() => {
    if (data && !inited) {
      if (data.default_zone && data.default_zone !== "All") setZone(data.default_zone);
      setInited(true);
    }
  }, [data, inited]);

  const branches = useMemo(() => {
    if (!data?.ready) return ["All"];
    const s = new Set();
    data.dashboard.forEach((r) => s.add(r.branch));
    data.large_deals.forEach((d) => d.branch && s.add(d.branch));
    return ["All", ...[...s].sort()];
  }, [data]);

  const persons = useMemo(() => {
    if (!data?.ready) return ["All"];
    const s = new Set();
    [...(data.large_deals || []), ...(data.win_loss || []), ...(data.bd_leads || [])]
      .forEach((d) => d.sales_member && s.add(d.sales_member));
    return ["All", ...[...s].sort()];
  }, [data]);

  const f = useMemo(() => {
    if (!data?.ready) return null;
    const mz = (z) => zone === "All" || (z || "") === zone;
    const mb = (b) => branch === "All" || (b || "").toLowerCase().startsWith(branch.toLowerCase().slice(0, 4));
    const mp = (p) => product === "All" || (p || "").toUpperCase().includes(product.toUpperCase());
    const mm = (m) => person === "All" || (m || "").toLowerCase().includes(person.toLowerCase());
    return {
      dash: data.dashboard.filter((r) => mz(r.zone) && mb(r.branch) && (segment === "All" || r.segment === segment)),
      deals: data.large_deals.filter((d) => mz(d.zone) && mb(d.branch) && mp(d.product) && mm(d.sales_member)),
      losses: data.win_loss.filter((w) => mz(w.zone) && mb(w.branch) && mm(w.sales_member)),
      bd: data.bd_leads.filter((b) => mb(b.branch) && mm(b.sales_member)),
      dcrTeams: (data.dcr?.teams || []).filter((t) => zone === "All" || DCR_ZONE[t.team.trim()] === zone),
      dcrWeeks: data.dcr?.weeks || [],
      dealers: (data.dealer_master || []).filter((m) => zone === "All" || m.zone === zone),
      dealerPlans: (data.dealer_plans || []).filter((p) => mz(p.zone)),
      dealerFunnel: (data.dealer_funnel || []).filter((d) => mm(d.sales_member)),
      crm: (data.crm_pipeline || []).filter((p) => mz(p.zone)),
      funnel: {
        universe: (data.crm_funnel?.universe || []).filter((u) => mz(u.zone)),
        leads: (data.crm_funnel?.leads || []).filter((l) => mz(l.zone)),
        opportunities: (data.crm_funnel?.opportunities || []).filter((o) => mz(o.zone)),
      },
      erp: (data.erp_billing || []).filter((e) => mz(e.region)),
    };
  }, [data, zone, branch, segment, person, product]);

  if (isLoading) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  if (!data?.ready) {
    const si = data?.sync_info || {};
    return (
      <div>
        <InvoicedSalesSection />
        <div className="py-16 text-center" data-testid="mis-analytics-empty">
        <Cloud className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500 font-medium">Waiting for the first SharePoint sync…</p>
        <p className="text-xs text-slate-400 mt-1">The analytics build automatically from the MIS workbook. Ask an admin to run "Sync now" in SharePoint Sync settings.</p>
        <div className="text-xs mt-4 space-y-1" data-testid="mis-analytics-sync-info">
          {!si.configured && <p className="text-amber-600 font-medium">No SharePoint link is configured yet in this environment.</p>}
          {si.configured && !si.enabled && <p className="text-amber-600 font-medium">A link is configured but auto-sync is off — open SharePoint Sync settings and press "Save & Sync" or "Sync now".</p>}
          {si.last_status === "error" && si.last_error && <p className="text-red-600 font-medium max-w-xl mx-auto">Last sync failed: {si.last_error}</p>}
          {si.last_status === "ok" && si.analytics_error && <p className="text-red-600 font-medium max-w-xl mx-auto">Sheets synced, but analytics parsing failed: {si.analytics_error}</p>}
          {si.last_sync_at && <p className="text-slate-400">Last sync attempt: {timeAgo(si.last_sync_at)}</p>}
        </div>
        </div>
      </div>
    );
  }

  const dirty = zone !== "All" || branch !== "All" || segment !== "All" || person !== "All" || product !== "All";

  return (
    <div data-testid="mis-analytics">
      <InvoicedSalesSection />
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <Filter label="Zone" value={zone} onChange={setZone} options={ZONES} testId="mis-filter-zone" />
        <Filter label="Branch" value={branch} onChange={setBranch} options={branches} testId="mis-filter-branch" width="w-[150px]" />
        <Filter label="Segment" value={segment} onChange={setSegment} options={SEGMENTS} testId="mis-filter-segment" />
        <Filter label="Person" value={person} onChange={setPerson} options={persons} testId="mis-filter-person" width="w-[160px]" />
        <Filter label="Product" value={product} onChange={setProduct} options={PRODUCTS} testId="mis-filter-product" />
        {dirty && (
          <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs text-slate-500"
            onClick={() => { setZone("All"); setBranch("All"); setSegment("All"); setPerson("All"); setProduct("All"); }}
            data-testid="mis-filter-reset">
            <FilterX className="h-3.5 w-3.5" /> Reset
          </Button>
        )}
        <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-1" data-testid="mis-analytics-live-chip">
          <Cloud className="h-3 w-3" /> Live from SharePoint · {data.parsed_at ? timeAgo(data.parsed_at) : "—"}
        </span>
      </div>

      <MISOverview dash={f.dash} deals={f.deals} losses={f.losses} product={product} />
      <MISPipelineSection dash={f.dash} deals={f.deals} crm={f.crm} funnel={f.funnel} zone={zone} product={product} />
      <MISInsights losses={f.losses} dcrTeams={f.dcrTeams} dcrWeeks={f.dcrWeeks} bd={f.bd}
        dealers={f.dealers} dealerPlans={f.dealerPlans} dealerFunnel={f.dealerFunnel} />
    </div>
  );
}
